/**
 * useAdminAssets — Admin-Web hooks for Issue #364 (administrative fixed
 * asset management + offboarding asset-return checks).
 *
 * 红线 (AI GUIDE §6 / §7):
 *   - 仅使用 generated @jinzhuan/api-client；不手写 fetch、不构造假状态。
 *   - 金额字段 originalCost / depreciationAmount / residualValue 全程以
 *     decimal-safe string 流转；前端不允许 Number() 化。
 *   - 错误信息走统一文案，409 提示乐观锁冲突，403 提示无权限。
 */
import { useCallback, useState } from 'react';
import { createApiClient } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type AdminAssetDto = ApiComponents['schemas']['AdminAssetDto'];
export type AdminAssetListDto =
  ApiComponents['schemas']['AdminAssetListDto'];
export type AdminAssetEventDto =
  ApiComponents['schemas']['AdminAssetEventDto'];
export type AdminAssetEventListDto =
  ApiComponents['schemas']['AdminAssetEventListDto'];
export type AdminAssetOffboardingCheckDto =
  ApiComponents['schemas']['AdminAssetOffboardingCheckDto'];
export type AdminAssetOffboardingCheckListDto =
  ApiComponents['schemas']['AdminAssetOffboardingCheckListDto'];
export type CreateAdminAssetDto =
  ApiComponents['schemas']['CreateAdminAssetDto'];
export type UpdateAdminAssetDto =
  ApiComponents['schemas']['UpdateAdminAssetDto'];
export type AssignAdminAssetDto =
  ApiComponents['schemas']['AssignAdminAssetDto'];
export type ReturnAdminAssetDto =
  ApiComponents['schemas']['ReturnAdminAssetDto'];
export type RepairAdminAssetDto =
  ApiComponents['schemas']['RepairAdminAssetDto'];
export type RetireAdminAssetDto =
  ApiComponents['schemas']['RetireAdminAssetDto'];
export type LostAdminAssetDto =
  ApiComponents['schemas']['LostAdminAssetDto'];
export type ArchiveAdminAssetDto =
  ApiComponents['schemas']['ArchiveAdminAssetDto'];

export const ADMIN_ASSET_STATUS_LABELS: Readonly<
  Record<AdminAssetDto['status'], string>
> = {
  available: '在库可用',
  assigned: '已领用',
  returned: '已归还',
  under_repair: '维修中',
  retired: '已报废',
  lost: '遗失',
  archived: '已归档',
};

export const ADMIN_ASSET_CATEGORY_LABELS: Readonly<
  Record<AdminAssetDto['category'], string>
> = {
  computer: '电脑/笔记本',
  workstation: '工位/桌椅',
  office_equipment: '办公设备',
  communication: '通讯/打印',
  access_card: '门禁/工牌',
  furniture: '家具',
  other: '其他',
};

export const ADMIN_ASSET_DEPRECIATION_METHOD_LABELS: Readonly<
  Record<AdminAssetDto['depreciationMethod'], string>
> = {
  none: '不计提',
  straight_line: '直线法',
  manual: '手工录入',
};

export interface AdminAssetsApiError {
  readonly message: string;
  readonly status?: number;
}

function toApiError(err: unknown, status?: number): AdminAssetsApiError {
  if (status === 409) {
    return { message: '记录已被其他操作修改或编码冲突，请刷新后重试', status };
  }
  if (status === 403) return { message: '没有权限执行此操作', status };
  if (status === 400) {
    return { message: '请求参数不合法，请检查必填项与字段格式', status };
  }
  if (status === 404) return { message: '资产不存在或不可访问', status };
  if (err instanceof Error) return { message: err.message, status };
  return { message: String(err), status };
}

export interface ListAdminAssetFilter {
  search?: string;
  status?: AdminAssetDto['status'];
  category?: AdminAssetDto['category'];
  custodianEmployeeId?: string;
  includeArchived?: boolean;
  page?: number;
  limit?: number;
}

export function useAdminAssets() {
  const [items, setItems] = useState<AdminAssetDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AdminAssetsApiError | null>(null);

  const fetchAssets = useCallback(async (filter?: ListAdminAssetFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, unknown> = {};
      if (filter?.search) query.search = filter.search;
      if (filter?.status) query.status = filter.status;
      if (filter?.category) query.category = filter.category;
      if (filter?.custodianEmployeeId) {
        query.custodianEmployeeId = filter.custodianEmployeeId;
      }
      if (typeof filter?.includeArchived === 'boolean') {
        query.includeArchived = filter.includeArchived;
      }
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/admin/assets',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) {
        setItems([...data.items]);
        setTotal(data.total);
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { items, total, loading, error, fetchAssets };
}

export function useAdminAssetDetail() {
  const [item, setItem] = useState<AdminAssetDto | null>(null);
  const [events, setEvents] = useState<AdminAssetEventDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AdminAssetsApiError | null>(null);

  const fetchAsset = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/admin/assets/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) setItem(data);
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async (id: string) => {
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/admin/assets/{id}/events',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) setEvents([...data.items]);
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    }
  }, []);

  return { item, events, loading, error, fetchAsset, fetchEvents };
}

export function useAdminAssetMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AdminAssetsApiError | null>(null);

  const wrap = useCallback(
    async <T>(fn: () => Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await fn();
        if (apiErr) {
          setError(toApiError(apiErr, response?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createAsset = useCallback(
    (body: CreateAdminAssetDto) =>
      wrap<AdminAssetDto>(() =>
        apiClient.POST('/api/v1/admin/assets', { body }) as Promise<{
          data?: AdminAssetDto;
          error?: unknown;
          response: Response;
        }>,
      ),
    [wrap],
  );

  const updateAsset = useCallback(
    (id: string, body: UpdateAdminAssetDto) =>
      wrap<AdminAssetDto>(() =>
        apiClient.PATCH('/api/v1/admin/assets/{id}', {
          params: { path: { id } },
          body,
        }) as Promise<{
          data?: AdminAssetDto;
          error?: unknown;
          response: Response;
        }>,
      ),
    [wrap],
  );

  const assignAsset = useCallback(
    (id: string, body: AssignAdminAssetDto) =>
      wrap<AdminAssetDto>(() =>
        apiClient.POST('/api/v1/admin/assets/{id}/assign', {
          params: { path: { id } },
          body,
        }) as Promise<{
          data?: AdminAssetDto;
          error?: unknown;
          response: Response;
        }>,
      ),
    [wrap],
  );

  const returnAsset = useCallback(
    (id: string, body: ReturnAdminAssetDto) =>
      wrap<AdminAssetDto>(() =>
        apiClient.POST('/api/v1/admin/assets/{id}/return', {
          params: { path: { id } },
          body,
        }) as Promise<{
          data?: AdminAssetDto;
          error?: unknown;
          response: Response;
        }>,
      ),
    [wrap],
  );

  const sendToRepair = useCallback(
    (id: string, body: RepairAdminAssetDto) =>
      wrap<AdminAssetDto>(() =>
        apiClient.POST('/api/v1/admin/assets/{id}/repair/start', {
          params: { path: { id } },
          body,
        }) as Promise<{
          data?: AdminAssetDto;
          error?: unknown;
          response: Response;
        }>,
      ),
    [wrap],
  );

  const completeRepair = useCallback(
    (id: string, body: RepairAdminAssetDto) =>
      wrap<AdminAssetDto>(() =>
        apiClient.POST('/api/v1/admin/assets/{id}/repair/complete', {
          params: { path: { id } },
          body,
        }) as Promise<{
          data?: AdminAssetDto;
          error?: unknown;
          response: Response;
        }>,
      ),
    [wrap],
  );

  const retireAsset = useCallback(
    (id: string, body: RetireAdminAssetDto) =>
      wrap<AdminAssetDto>(() =>
        apiClient.POST('/api/v1/admin/assets/{id}/retire', {
          params: { path: { id } },
          body,
        }) as Promise<{
          data?: AdminAssetDto;
          error?: unknown;
          response: Response;
        }>,
      ),
    [wrap],
  );

  const markLost = useCallback(
    (id: string, body: LostAdminAssetDto) =>
      wrap<AdminAssetDto>(() =>
        apiClient.POST('/api/v1/admin/assets/{id}/lost', {
          params: { path: { id } },
          body,
        }) as Promise<{
          data?: AdminAssetDto;
          error?: unknown;
          response: Response;
        }>,
      ),
    [wrap],
  );

  const archiveAsset = useCallback(
    (id: string, body: ArchiveAdminAssetDto) =>
      wrap<AdminAssetDto>(() =>
        apiClient.POST('/api/v1/admin/assets/{id}/archive', {
          params: { path: { id } },
          body,
        }) as Promise<{
          data?: AdminAssetDto;
          error?: unknown;
          response: Response;
        }>,
      ),
    [wrap],
  );

  return {
    loading,
    error,
    createAsset,
    updateAsset,
    assignAsset,
    returnAsset,
    sendToRepair,
    completeRepair,
    retireAsset,
    markLost,
    archiveAsset,
  };
}

export function useAdminAssetOffboardingChecks() {
  const [items, setItems] = useState<AdminAssetOffboardingCheckDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AdminAssetsApiError | null>(null);

  const fetchChecks = useCallback(async (caseId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/admin/assets/offboarding/{caseId}/checks',
        { params: { path: { caseId } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) setItems([...data.items]);
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const generateChecks = useCallback(async (caseId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/admin/assets/offboarding/{caseId}/checks/generate',
        { params: { path: { caseId } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) setItems([...data.items]);
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { items, loading, error, fetchChecks, generateChecks };
}
